import { initReactI18next } from 'react-i18next'

import i18n, { type TOptions } from 'i18next'

const originalT = i18n.t.bind(i18n)

export const snakeToCamel = (str: string): string => {
  if (str === str.toUpperCase() && str.includes('_')) {
    return str
  }
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

i18n.t = ((key: string | string[], options?: TOptions) => {
  if (typeof key === 'string') {
    const transformedKey = snakeToCamel(key)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (originalT as any)(transformedKey, options)
  }
  if (Array.isArray(key)) {
    const transformedKeys = key.map(k => (typeof k === 'string' ? snakeToCamel(k) : k))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (originalT as any)(transformedKeys, options)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (originalT as any)(key, options)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) as any

i18n.use(initReactI18next).init({
  lng: 'en',
  fallbackLng: 'en',
  ns: ['translation'],
  defaultNS: 'translation',
  keySeparator: false, // Important: désactive le séparateur de clé
  nsSeparator: false, // Important: désactive le séparateur de namespace
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: true,
  },
  // IMPORTANT en prod: pas d’écriture des clés manquantes
  saveMissing: false,
  resources: {
    en: {
      translation: {
        // Common keys
        'common.loading.app': 'Loading',
        'common.isLoading': 'Is Loading...',
        'common.noAccess.text1': "Sorry, you don't have access to this page.",
        'common.noAccess.text2': 'To request access, please contact: ',
        'common.translatorMode': 'Translator mode',

        // Login keys
        'login.title': 'ARDIAN',
        'login.subtitle': 'Good to see you back on Trustview backoffice',
        'login.youAre': 'You are',
        'login.admin': 'an external administrator',
        'login.internal': 'an Ardian employee',
        'login.general': 'an Ardian employee or an administrator',
        'login.signIn': 'SIGN IN',

        // App loading failure keys — rendered by ErrorLoadingApp, which is displayed precisely
        // when the backend translation catalog failed to load. They MUST stay in these static
        // resources: without them the screen renders its raw keys to the user.
        // Values are copied VERBATIM from the reference catalog
        // (`src/__mocks__/translations.mock.ts:5033-5035`) so the fallback shows the same copy as
        // the app normally does. Never reword here — that would silently fork the two.
        'app.load.errorTitle': 'Something went wrong while loading the app',
        'app.load.errorSubtitle': 'Try refreshing the page.',
        'app.load.buttonLabel': 'REFRESH',

        // Error screen keys — rendered by the error screen, which `auth.provider` displays on an
        // authentication boot failure, i.e. before the backend catalog is fetched. Same rule as
        // `app.load.*` above: they MUST stay here or the screen renders its raw keys.
        // `common.returnToHome` is also used by the (post-session) Unauthorized screen, where the
        // remote catalog covers it — it is duplicated here for the pre-session path only.
        // Values are copied VERBATIM from the reference catalog
        // (`src/__mocks__/translations.mock.ts`). Never reword here — that would silently fork the
        // two.
        'app.error.notFoundTitle': 'Page not found',
        'common.returnToHome': 'Back to home',

        // Callback keys
        'callback.pageName': 'Authentication callback Page',

        // Error keys
        'error.timeout':
          'This action is taking longer than expected but is still being processed. The data will refresh automatically — no need to do anything.',
        'error.network': 'A network error occurred. Please check your connection and try again.',
        'error.maintenance':
          'The application is temporarily under maintenance. Please try again in a few minutes.',

        // Maintenance keys
        'maintenance.title': 'Maintenance in progress',
        'maintenance.message':
          'The application is temporarily unavailable due to maintenance. This page will automatically resume as soon as the service is back online — no action is needed on your part.',

        // Progress spinner keys
        'progressSpinnerLoadedContent.authSystem': 'Authentication system',

        // Document keys
        'document.messages.fileUrlMissing': 'This document has no file to preview.',
      },
    },
  },
})

export default i18n
